import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PlusIcon,
  MagnifyingGlassIcon,

  EllipsisHorizontalIcon,
  DocumentTextIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  ChartBarIcon,

} from '@heroicons/react/24/outline';
import { Menu, Transition, Dialog } from '@headlessui/react';
import { Fragment } from 'react';
import { backendAPI } from '../../utils/api';
// LoadingSpinner component - inline for now
const LoadingSpinner = () => (
  <div className="flex items-center justify-center p-4">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
  </div>
);
import { Contract, ContractFilters, ContractStats, ContractStatus, ContractType } from '../../types/contracts';
import { formatCurrency, formatDate, formatPercentage } from '../../utils/format';

const ContractsPage: React.FC = () => {
  const [filters, setFilters] = useState<ContractFilters>({
    page: 1,
    limit: 10,
    sort_by: 'created_at',
    sort_order: 'desc',
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedContract, setSelectedContract] = useState<Contract | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const queryClient = useQueryClient();

  // Fetch contracts
  const { data: contractsData, isLoading: contractsLoading } = useQuery({
    queryKey: ['contracts', filters],
    queryFn: () => backendAPI.getContracts(filters),
  });

  // Fetch contract stats
  const { data: statsData } = useQuery({
    queryKey: ['contract-stats'],
    queryFn: () => backendAPI.getContractStats(),
  });

  // Mutations
  const deleteContractMutation = useMutation({
    mutationFn: (id: string) => backendAPI.deleteContract(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      queryClient.invalidateQueries({ queryKey: ['contract-stats'] });
    },
  });

  const approveContractMutation = useMutation({
    mutationFn: (id: string) => backendAPI.approveContract(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      queryClient.invalidateQueries({ queryKey: ['contract-stats'] });
    },
  });

  const activateContractMutation = useMutation({
    mutationFn: (id: string) => backendAPI.activateContract(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      queryClient.invalidateQueries({ queryKey: ['contract-stats'] });
    },
  });

  const contracts = contractsData?.data || [];
  const pagination = contractsData?.pagination || {};
  const stats = statsData as ContractStats;

  // Utility functions
  const getStatusColor = (status: ContractStatus) => {
    switch (status) {
      case 'ACTIVE':
        return 'bg-green-100 text-green-800';
      case 'DRAFT':
        return 'bg-gray-100 text-gray-800';
      case 'PENDING_APPROVAL':
        return 'bg-yellow-100 text-yellow-800';
      case 'EXPIRED':
        return 'bg-red-100 text-red-800';
      case 'PAUSED':
        return 'bg-orange-100 text-orange-800';
      case 'TERMINATED':
        return 'bg-red-100 text-red-800';
      case 'COMPLETED':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeColor = (type: ContractType) => {
    switch (type) {
      case 'RETAINER':
        return 'bg-purple-100 text-purple-800';
      case 'FIXED_PRICE':
        return 'bg-blue-100 text-blue-800';
      case 'TIME_AND_MATERIALS':
        return 'bg-green-100 text-green-800';
      case 'RECURRING_SERVICE':
        return 'bg-indigo-100 text-indigo-800';
      case 'MAINTENANCE':
        return 'bg-orange-100 text-orange-800';
      case 'SUPPORT':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleSearch = (value: string) => {
    setFilters(prev => ({ ...prev, search: value, page: 1 }));
  };

  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handleSort = (field: string) => {
    setFilters(prev => ({
      ...prev,
      sort_by: field,
      sort_order: prev.sort_by === field && prev.sort_order === 'asc' ? 'desc' : 'asc',
    }));
  };

  const openContractDetails = (contract: Contract) => {
    setSelectedContract(contract);
    setShowDetailsModal(true);
  };

  if (contractsLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Contract Management</h1>
          <p className="text-gray-600">Manage retainers, contracts, and client agreements</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          New Contract
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <DocumentTextIcon className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Contracts</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total_contracts}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-8 w-8 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Active Contracts</p>
                <p className="text-2xl font-bold text-gray-900">{stats.active_contracts}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CurrencyDollarIcon className="h-8 w-8 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Value</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.total_value)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ChartBarIcon className="h-8 w-8 text-orange-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Utilization</p>
                <p className="text-2xl font-bold text-gray-900">{formatPercentage(stats.retainer_utilization)}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          {/* Search */}
          <div className="relative flex-1 lg:max-w-md">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search contracts..."
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={filters.search || ''}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-4">
            <select
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={filters.status || ''}
              onChange={(e) => handleFilterChange('status', e.target.value || undefined)}
            >
              <option value="">All Statuses</option>
              <option value="ACTIVE">Active</option>
              <option value="DRAFT">Draft</option>
              <option value="PENDING_APPROVAL">Pending Approval</option>
              <option value="EXPIRED">Expired</option>
              <option value="PAUSED">Paused</option>
              <option value="TERMINATED">Terminated</option>
              <option value="COMPLETED">Completed</option>
            </select>

            <select
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={filters.contract_type || ''}
              onChange={(e) => handleFilterChange('contract_type', e.target.value || undefined)}
            >
              <option value="">All Types</option>
              <option value="RETAINER">Retainer</option>
              <option value="FIXED_PRICE">Fixed Price</option>
              <option value="TIME_AND_MATERIALS">Time & Materials</option>
              <option value="RECURRING_SERVICE">Recurring Service</option>
              <option value="MAINTENANCE">Maintenance</option>
              <option value="SUPPORT">Support</option>
            </select>
          </div>
        </div>
      </div>

      {/* Contracts Table */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('contract_number')}
                >
                  Contract #
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('title')}
                >
                  Title
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Client
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Value
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('start_date')}
                >
                  Start Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {contracts.map((contract: Contract) => (
                <tr key={contract.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {contract.contract_number}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div
                      className="text-sm font-medium text-blue-600 hover:text-blue-800 cursor-pointer"
                      onClick={() => openContractDetails(contract)}
                    >
                      {contract.title}
                    </div>
                    {contract.description && (
                      <div className="text-sm text-gray-500">{contract.description}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {contract.client?.name || 'N/A'}
                    </div>
                    {contract.client?.company && (
                      <div className="text-sm text-gray-500">{contract.client.company}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(contract.contract_type)}`}>
                      {contract.contract_type.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(contract.status)}`}>
                      {contract.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {contract.contract_value ? formatCurrency(contract.contract_value) : 'N/A'}
                    </div>
                    {contract.retainer_amount && (
                      <div className="text-sm text-gray-500">
                        {formatCurrency(contract.retainer_amount)}/period
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(contract.start_date)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <Menu as="div" className="relative inline-block text-left">
                      <Menu.Button className="inline-flex justify-center w-full rounded-md border border-gray-300 shadow-sm px-2 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                        <EllipsisHorizontalIcon className="h-5 w-5" />
                      </Menu.Button>

                      <Transition
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                      >
                        <Menu.Items className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-10">
                          <div className="py-1">
                            <Menu.Item>
                              {({ active }) => (
                                <button
                                  onClick={() => openContractDetails(contract)}
                                  className={`${
                                    active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                                  } group flex items-center px-4 py-2 text-sm w-full text-left`}
                                >
                                  <DocumentTextIcon className="mr-3 h-5 w-5" />
                                  View Details
                                </button>
                              )}
                            </Menu.Item>

                            {contract.status === 'DRAFT' && (
                              <Menu.Item>
                                {({ active }) => (
                                  <button
                                    onClick={() => approveContractMutation.mutate(contract.id)}
                                    className={`${
                                      active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                                    } group flex items-center px-4 py-2 text-sm w-full text-left`}
                                  >
                                    <CheckCircleIcon className="mr-3 h-5 w-5" />
                                    Approve
                                  </button>
                                )}
                              </Menu.Item>
                            )}

                            {contract.status === 'PENDING_APPROVAL' && (
                              <Menu.Item>
                                {({ active }) => (
                                  <button
                                    onClick={() => activateContractMutation.mutate(contract.id)}
                                    className={`${
                                      active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                                    } group flex items-center px-4 py-2 text-sm w-full text-left`}
                                  >
                                    <ArrowPathIcon className="mr-3 h-5 w-5" />
                                    Activate
                                  </button>
                                )}
                              </Menu.Item>
                            )}

                            {contract.status === 'DRAFT' && (
                              <Menu.Item>
                                {({ active }) => (
                                  <button
                                    onClick={() => deleteContractMutation.mutate(contract.id)}
                                    className={`${
                                      active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                                    } group flex items-center px-4 py-2 text-sm w-full text-left text-red-600`}
                                  >
                                    <XCircleIcon className="mr-3 h-5 w-5" />
                                    Delete
                                  </button>
                                )}
                              </Menu.Item>
                            )}
                          </div>
                        </Menu.Items>
                      </Transition>
                    </Menu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handleFilterChange('page', Math.max(1, filters.page! - 1))}
                disabled={filters.page === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => handleFilterChange('page', Math.min(pagination.total_pages, filters.page! + 1))}
                disabled={filters.page === pagination.total_pages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing{' '}
                  <span className="font-medium">
                    {(filters.page! - 1) * filters.limit! + 1}
                  </span>{' '}
                  to{' '}
                  <span className="font-medium">
                    {Math.min(filters.page! * filters.limit!, pagination.total)}
                  </span>{' '}
                  of <span className="font-medium">{pagination.total}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => handleFilterChange('page', Math.max(1, filters.page! - 1))}
                    disabled={filters.page === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => handleFilterChange('page', Math.min(pagination.total_pages, filters.page! + 1))}
                    disabled={filters.page === pagination.total_pages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Contract Details Modal */}
      {selectedContract && (
        <ContractDetailsModal
          contract={selectedContract}
          isOpen={showDetailsModal}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedContract(null);
          }}
        />
      )}

      {/* Create Contract Modal */}
      <CreateContractModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => {
          setShowCreateModal(false);
          queryClient.invalidateQueries({ queryKey: ['contracts'] });
          queryClient.invalidateQueries({ queryKey: ['contract-stats'] });
        }}
      />
    </div>
  );
};

// Contract Details Modal Component
const ContractDetailsModal: React.FC<{
  contract: Contract;
  isOpen: boolean;
  onClose: () => void;
}> = ({ contract, isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('overview');

  const tabs = [
    { id: 'overview', name: 'Overview', icon: DocumentTextIcon },
    { id: 'periods', name: 'Periods', icon: CalendarIcon },
    { id: 'consumption', name: 'Consumption', icon: ChartBarIcon },
    { id: 'rates', name: 'Rate Cards', icon: CurrencyDollarIcon },
  ];

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="w-full max-w-4xl bg-white rounded-xl shadow-lg max-h-[90vh] overflow-hidden">
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <Dialog.Title className="text-lg font-semibold text-gray-900">
                    {contract.title}
                  </Dialog.Title>
                  <p className="text-sm text-gray-500">{contract.contract_number}</p>
                </div>
                <button
                  onClick={onClose}
                  className="rounded-md text-gray-400 hover:text-gray-600"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>

              {/* Tabs */}
              <div className="mt-4">
                <nav className="flex space-x-8">
                  {tabs.map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center py-2 px-1 text-sm font-medium border-b-2 transition-colors ${
                          activeTab === tab.id
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <Icon className="h-5 w-5 mr-2" />
                        {tab.name}
                      </button>
                    );
                  })}
                </nav>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {activeTab === 'overview' && (
                <ContractOverviewTab contract={contract} />
              )}
              {activeTab === 'periods' && (
                <ContractPeriodsTab contract={contract} />
              )}
              {activeTab === 'consumption' && (
                <ContractConsumptionTab contract={contract} />
              )}
              {activeTab === 'rates' && (
                <ContractRatesTab contract={contract} />
              )}
            </div>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

// Overview Tab Component
const ContractOverviewTab: React.FC<{ contract: Contract }> = ({ contract }) => {
  return (
    <div className="space-y-6">
      {/* Basic Information */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">Contract Details</h3>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-gray-500">Client</label>
              <p className="text-sm text-gray-900">{contract.client?.name}</p>
              {contract.client?.company && (
                <p className="text-sm text-gray-500">{contract.client.company}</p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Type</label>
              <p className="text-sm text-gray-900">{contract.contract_type.replace('_', ' ')}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Status</label>
              <p className="text-sm text-gray-900">{contract.status.replace('_', ' ')}</p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">Financial Terms</h3>
          <div className="space-y-3">
            {contract.contract_value && (
              <div>
                <label className="text-sm font-medium text-gray-500">Contract Value</label>
                <p className="text-sm text-gray-900">{formatCurrency(contract.contract_value)}</p>
              </div>
            )}
            {contract.retainer_amount && (
              <div>
                <label className="text-sm font-medium text-gray-500">Retainer Amount</label>
                <p className="text-sm text-gray-900">{formatCurrency(contract.retainer_amount)}</p>
              </div>
            )}
            {contract.included_hours && (
              <div>
                <label className="text-sm font-medium text-gray-500">Included Hours</label>
                <p className="text-sm text-gray-900">{contract.included_hours} hours</p>
              </div>
            )}
            {contract.overage_rate && (
              <div>
                <label className="text-sm font-medium text-gray-500">Overage Rate</label>
                <p className="text-sm text-gray-900">{formatCurrency(contract.overage_rate)}/hour</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Scope of Work */}
      {contract.scope_of_work && (
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-3">Scope of Work</h3>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{contract.scope_of_work}</p>
          </div>
        </div>
      )}

      {/* Terms and Conditions */}
      {contract.terms_and_conditions && (
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-3">Terms and Conditions</h3>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{contract.terms_and_conditions}</p>
          </div>
        </div>
      )}
    </div>
  );
};

// Periods Tab Component
const ContractPeriodsTab: React.FC<{ contract: Contract }> = ({ contract }) => {
  const periods = contract.periods || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">Contract Periods</h3>
      </div>

      {periods.length === 0 ? (
        <div className="text-center py-8">
          <CalendarIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No periods found</h3>
          <p className="mt-1 text-sm text-gray-500">Contract periods will appear here once created.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {periods.map((period) => (
            <div key={period.id} className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-gray-900">
                  Period {period.period_number}
                </h4>
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  period.status === 'ACTIVE' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {period.status}
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-xs font-medium text-gray-500">Period</label>
                  <p className="text-sm text-gray-900">
                    {formatDate(period.start_date)} - {formatDate(period.end_date)}
                  </p>
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-500">Hours</label>
                  <p className="text-sm text-gray-900">
                    {period.consumed_hours} / {period.allocated_hours}
                    {period.rollover_hours > 0 && (
                      <span className="text-blue-600"> (+{period.rollover_hours} rollover)</span>
                    )}
                  </p>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                    <div
                      className="bg-blue-600 h-2 rounded-full"
                      style={{
                        width: `${Math.min(100, (period.consumed_hours / period.allocated_hours) * 100)}%`
                      }}
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-500">Amount</label>
                  <p className="text-sm text-gray-900">
                    {formatCurrency(period.consumed_amount)} / {formatCurrency(period.allocated_amount)}
                  </p>
                  {period.invoice && (
                    <p className="text-xs text-green-600">
                      Invoiced: {period.invoice.invoice_number}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Consumption Tab Component
const ContractConsumptionTab: React.FC<{ contract: Contract }> = ({ contract }) => {
  const { data: consumption } = useQuery({
    queryKey: ['contract-consumption', contract.id],
    queryFn: () => backendAPI.getContractConsumption(contract.id),
  });

  const consumptionData = consumption || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">Consumption History</h3>
      </div>

      {consumptionData.length === 0 ? (
        <div className="text-center py-8">
          <ChartBarIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No consumption recorded</h3>
          <p className="mt-1 text-sm text-gray-500">Time and work consumption will appear here.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {consumptionData.map((item: any) => (
            <div key={item.id} className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    item.consumption_type === 'HOURS' ? 'bg-blue-100 text-blue-800' :
                    item.consumption_type === 'AMOUNT' ? 'bg-green-100 text-green-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {item.consumption_type}
                  </span>
                  <span className="text-sm font-medium text-gray-900">
                    {item.quantity} {item.consumption_type.toLowerCase()}
                  </span>
                </div>
                <div className="text-sm text-gray-500">
                  {formatDate(item.consumed_at)}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-gray-500">Description</label>
                  <p className="text-sm text-gray-900">{item.description || 'No description'}</p>
                  
                  {item.time_entry && (
                    <p className="text-xs text-blue-600 mt-1">
                      Linked to time entry: {item.time_entry.description}
                    </p>
                  )}
                  {item.project && (
                    <p className="text-xs text-blue-600 mt-1">
                      Project: {item.project.name}
                    </p>
                  )}
                  {item.task && (
                    <p className="text-xs text-blue-600 mt-1">
                      Task: {item.task.title}
                    </p>
                  )}
                  {item.ticket && (
                    <p className="text-xs text-blue-600 mt-1">
                      Ticket: {item.ticket.title}
                    </p>
                  )}
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-500">Amount</label>
                  <p className="text-sm text-gray-900">{formatCurrency(item.amount)}</p>
                  {item.rate && (
                    <p className="text-xs text-gray-500">Rate: {formatCurrency(item.rate)}</p>
                  )}
                  
                  <div className="flex items-center mt-2">
                    {item.approved ? (
                      <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                        <CheckCircleIcon className="h-3 w-3 mr-1" />
                        Approved
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                        <ClockIcon className="h-3 w-3 mr-1" />
                        Pending
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Rates Tab Component
const ContractRatesTab: React.FC<{ contract: Contract }> = ({ contract }) => {
  const { data: rateCards } = useQuery({
    queryKey: ['contract-rate-cards', contract.id],
    queryFn: () => backendAPI.getContractRateCards(contract.id),
  });

  const rateCardsData = rateCards || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">Rate Cards</h3>
      </div>

      {rateCardsData.length === 0 ? (
        <div className="text-center py-8">
          <CurrencyDollarIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No rate cards found</h3>
          <p className="mt-1 text-sm text-gray-500">Custom rate cards will appear here.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {rateCardsData.map((rateCard: any) => (
            <div key={rateCard.id} className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="text-lg font-medium text-gray-900">{rateCard.name}</h4>
                  {rateCard.description && (
                    <p className="text-sm text-gray-500">{rateCard.description}</p>
                  )}
                </div>
                <div className="text-sm text-gray-500">
                  Effective: {formatDate(rateCard.effective_date)}
                  {rateCard.end_date && ` - ${formatDate(rateCard.end_date)}`}
                </div>
              </div>

              {rateCard.rate_items && rateCard.rate_items.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Service Type
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Role Level
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Hourly Rate
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Min Hours
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Retainer
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Overage Rate
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {rateCard.rate_items.map((item: any) => (
                        <tr key={item.id}>
                          <td className="px-4 py-2 text-sm text-gray-900">{item.service_type}</td>
                          <td className="px-4 py-2 text-sm text-gray-900">{item.role_level || '-'}</td>
                          <td className="px-4 py-2 text-sm text-gray-900">{formatCurrency(item.hourly_rate)}</td>
                          <td className="px-4 py-2 text-sm text-gray-900">{item.minimum_hours}</td>
                          <td className="px-4 py-2 text-sm text-gray-900">
                            {item.is_retainer_included ? 'Included' : 'Not Included'}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-900">
                            {item.overage_rate ? formatCurrency(item.overage_rate) : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Create Contract Modal Component (placeholder)
const CreateContractModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}> = ({ isOpen, onClose, onSuccess }) => {
  // This would be a comprehensive form for creating contracts
  // For now, it's a placeholder
  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="w-full max-w-2xl bg-white rounded-xl shadow-lg">
          <div className="p-6">
            <Dialog.Title className="text-lg font-semibold text-gray-900 mb-4">
              Create New Contract
            </Dialog.Title>
            <p className="text-gray-600 mb-4">
              Comprehensive contract creation form would go here with all the fields for creating contracts,
              including client selection, contract type, terms, financial details, etc.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={onSuccess}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Create Contract
              </button>
            </div>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default ContractsPage;